import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer, ScatterChart, Scatter
} from 'recharts';
import { TrendingDown, Warning, Users, BookOpen, DownloadSimple } from 'phosphor-react';
import analyticsService from '../services/analyticsService';
import { LoadingSpinner } from '../components/LoadingSpinner';
import DialogModal from '../components/DialogModal';
import './AnalyticsDashboardPage.css';

const AnalyticsDashboardPage = () => {
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('at-risk');
  const [atRiskStudents, setAtRiskStudents] = useState([]);
  const [classComparison, setClassComparison] = useState([]);
  const [studentAnalytics, setStudentAnalytics] = useState(null);
  const [selectedStudentId, setSelectedStudentId] = useState(null);
  const [academicTermId, setAcademicTermId] = useState(null);
  const [recalculatingAll, setRecalculatingAll] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState(null); // { title, message, type, confirmLabel, onConfirm }

  // Load at-risk students and class comparison
  useEffect(() => {
    const loadDashboardData = async () => {
      setLoading(true);
      try {
        const [atRiskRes, classRes] = await Promise.all([
          analyticsService.getAtRiskStudents(),
          analyticsService.getClassComparison()
        ]);

        setAtRiskStudents(atRiskRes.students || []);
        setClassComparison(classRes.classes || []);
        setAcademicTermId(atRiskRes.academic_term_id);
      } catch (err) {
        toast.error('Failed to load analytics data: ' + err.message);
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    loadDashboardData();
  }, []);

  // Load individual student analytics
  useEffect(() => {
    if (selectedStudentId) {
      const loadStudentAnalytics = async () => {
        setLoading(true);
        try {
          const res = await analyticsService.getStudentAnalytics(selectedStudentId, academicTermId);
          setStudentAnalytics(res);
        } catch (err) {
          toast.error('Failed to load student analytics: ' + err.message);
          console.error(err);
        } finally {
          setLoading(false);
        }
      };

      loadStudentAnalytics();
    }
  }, [selectedStudentId, academicTermId]);

  const handleRecalculateAll = () => {
    setConfirmDialog({
      title: 'Recalculate Analytics',
      message: 'Recalculate analytics for all students? This may take a moment.',
      type: 'warning',
      confirmLabel: 'Recalculate',
      onConfirm: async () => {
        setRecalculatingAll(true);
        try {
          const res = await analyticsService.recalculateAll(academicTermId);
          toast.success(`Recalculated analytics for ${res.students_calculated} students`);

          // Reload dashboard data
          const [atRiskRes, classRes] = await Promise.all([
            analyticsService.getAtRiskStudents(),
            analyticsService.getClassComparison()
          ]);

          setAtRiskStudents(atRiskRes.students || []);
          setClassComparison(classRes.classes || []);
        } catch (err) {
          toast.error('Failed to recalculate analytics: ' + err.message);
          console.error(err);
        } finally {
          setRecalculatingAll(false);
        }
      },
    });
  };

  const handleExportCSV = () => {
    let rows = [];
    let filename = 'analytics-export.csv';
    if (activeTab === 'at-risk') {
      rows = atRiskStudents.map((s) => ({ name: s.name, student_code: s.student_code, gpa: s.gpa, attendance_rate: s.attendance_rate, risk_level: s.risk_level, risk_factors: (s.risk_factors || []).join('; ') }));
      filename = 'at-risk-students.csv';
    } else if (activeTab === 'classes') {
      rows = classComparison.map((c) => ({ class_name: c.class_name, total_students: c.total_students, average_gpa: c.average_gpa, average_attendance: c.average_attendance, students_passing: c.students_passing, students_failing: c.students_failing, students_at_risk: c.students_at_risk, strongest_subject: c.strongest_subject, weakest_subject: c.weakest_subject }));
      filename = 'class-comparison.csv';
    }
    if (rows.length === 0) {
      toast.error('Nothing to export on this tab');
      return;
    }
    const columns = Object.keys(rows[0]);
    const csv = [columns.join(','), ...rows.map((r) => columns.map((c) => `"${String(r[c] ?? '').replace(/"/g, '""')}"`).join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const handleExportPDF = () => {
    window.print();
  };

  const getRiskColor = (level) => {
    switch(level) {
      case 'low': return '#10b981';
      case 'moderate': return '#f59e0b';
      case 'high': return '#ef4444';
      case 'critical': return '#7f1d1d';
      default: return '#6b7280';
    }
  };

  const getRiskLabel = (level) => {
    return level.charAt(0).toUpperCase() + level.slice(1);
  };

  const AtRiskDashboard = () => (
    <div className="analytics-tab-content">
      <div className="section">
        <h3>Students at Risk (Requiring Intervention)</h3>
        {atRiskStudents.length === 0 ? (
          <div className="empty-state">
            <Users size={48} weight="light" />
            <p>No students currently at risk</p>
          </div>
        ) : (
          <div className="students-list">
            {atRiskStudents.map((student) => (
              <div
                key={student.student_id}
                className={`risk-card risk-${student.risk_level}`}
                onClick={() => setSelectedStudentId(student.student_id)}
              >
                <div className="risk-header">
                  <div className="risk-info">
                    <h4>{student.name}</h4>
                    <p className="student-code">{student.student_code}</p>
                  </div>
                  <div className={`risk-badge risk-${student.risk_level}`}>
                    {getRiskLabel(student.risk_level)}
                  </div>
                </div>

                <div className="risk-metrics">
                  <div className="metric">
                    <span className="label">GPA</span>
                    <span className="value">{student.gpa ? student.gpa.toFixed(2) : 'N/A'}</span>
                  </div>
                  <div className="metric">
                    <span className="label">Attendance</span>
                    <span className="value">
                      {student.attendance_rate ? `${student.attendance_rate.toFixed(1)}%` : 'N/A'}
                    </span>
                  </div>
                </div>

                {student.risk_factors.length > 0 && (
                  <div className="risk-factors">
                    <span className="factors-label">Issues:</span>
                    <div className="factors">
                      {student.risk_factors.map((factor) => (
                        <span key={factor} className="factor">
                          {factor.replace(/_/g, ' ')}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  const ClassComparisonDashboard = () => {
    // Prepare data for class performance chart
    const chartData = classComparison.map(cls => ({
      name: cls.class_name,
      gpa: cls.average_gpa || 0,
      attendance: cls.average_attendance || 0,
      at_risk: cls.students_at_risk || 0
    }));

    return (
      <div className="analytics-tab-content">
        <div className="section">
          <h3>Class Performance Comparison</h3>
          
          {chartData.length === 0 ? (
            <div className="empty-state">
              <BookOpen size={48} weight="light" />
              <p>No class data available</p>
            </div>
          ) : (
            <>
              <div className="chart-container">
                <h4>Average GPA by Class</h4>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="gpa" fill="#3b82f6" name="Avg GPA" />
                  </BarChart>
                </ResponsiveContainer>
              </div>

              <div className="chart-container">
                <h4>Attendance Rate vs Students at Risk</h4>
                <ResponsiveContainer width="100%" height={300}>
                  <ScatterChart margin={{ top: 20, right: 20, bottom: 20, left: 20 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" dataKey="attendance" name="Attendance %" />
                    <YAxis type="number" dataKey="at_risk" name="Students at Risk" />
                    <Tooltip cursor={{ strokeDasharray: '3 3' }} />
                    <Scatter name="Classes" data={chartData} fill="#8b5cf6" />
                  </ScatterChart>
                </ResponsiveContainer>
              </div>

              <div className="class-details">
                <h4>Class Details</h4>
                <table className="classes-table">
                  <thead>
                    <tr>
                      <th>Class</th>
                      <th>Students</th>
                      <th>Avg GPA</th>
                      <th>Attendance</th>
                      <th>Passing</th>
                      <th>Failing</th>
                      <th>At Risk</th>
                      <th>Strongest Subject</th>
                      <th>Weakest Subject</th>
                    </tr>
                  </thead>
                  <tbody>
                    {classComparison.map((cls) => (
                      <tr key={cls.class_id}>
                        <td className="class-name">{cls.class_name}</td>
                        <td>{cls.total_students}</td>
                        <td>{cls.average_gpa ? cls.average_gpa.toFixed(2) : 'N/A'}</td>
                        <td>{cls.average_attendance ? `${cls.average_attendance.toFixed(1)}%` : 'N/A'}</td>
                        <td>{cls.students_passing}</td>
                        <td className="failing">{cls.students_failing}</td>
                        <td className="at-risk">{cls.students_at_risk}</td>
                        <td>{cls.strongest_subject || '—'}</td>
                        <td>{cls.weakest_subject || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      </div>
    );
  };

  const StudentDetailsDashboard = () => {
    if (!selectedStudentId || !studentAnalytics) {
      return (
        <div className="analytics-tab-content">
          <div className="empty-state">
            <p>Select a student from the at-risk list to view detailed analytics</p>
          </div>
        </div>
      );
    }

    const metrics = [
      { label: 'Overall GPA', value: studentAnalytics.overall_gpa, unit: '' },
      { label: 'Attendance Rate', value: studentAnalytics.attendance_rate, unit: '%' },
      { label: 'Assignment Completion', value: studentAnalytics.assignment_completion_rate, unit: '%' },
    ];

    return (
      <div className="analytics-tab-content">
        <div className="section">
          <div className="student-header">
            <div>
              <h3>{studentAnalytics.student_name}</h3>
              <p className="student-code">{studentAnalytics.student_id}</p>
            </div>
            <div className={`risk-badge risk-${studentAnalytics.risk_level}`}>
              {getRiskLabel(studentAnalytics.risk_level)}
            </div>
          </div>

          <div className="metrics-grid">
            {metrics.map((metric) => (
              <div key={metric.label} className="metric-card">
                <span className="metric-label">{metric.label}</span>
                <span className="metric-value">
                  {metric.value !== null && metric.value !== undefined
                    ? `${metric.value.toFixed(1)}${metric.unit}`
                    : 'N/A'}
                </span>
              </div>
            ))}
          </div>

          <div className="section">
            <h4>Subject Performance</h4>
            <div className="subject-performance">
              <div className="subject-card strong">
                <span className="subject-label">Best Subject</span>
                <span className="subject-name">{studentAnalytics.best_subject || 'N/A'}</span>
                <span className="subject-grade">Grade {studentAnalytics.best_subject_grade || '—'}</span>
              </div>
              <div className="subject-card weak">
                <span className="subject-label">Needs Improvement</span>
                <span className="subject-name">{studentAnalytics.worst_subject || 'N/A'}</span>
                <span className="subject-grade">Grade {studentAnalytics.worst_subject_grade || '—'}</span>
              </div>
            </div>
          </div>

          {studentAnalytics.risk_factors && studentAnalytics.risk_factors.length > 0 && (
            <div className="section">
              <h4>Risk Factors</h4>
              <div className="risk-factors-list">
                {studentAnalytics.risk_factors.map((factor) => (
                  <div key={factor} className="risk-item">
                    <Warning size={20} weight="fill" />
                    <span>{factor.replace(/_/g, ' ')}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="section">
            <h4>Trends</h4>
            <div className="trends-grid">
              <div className="trend-card">
                <span className="trend-label">GPA Trend</span>
                <span className="trend-value">{studentAnalytics.gpa_trend || 'stable'}</span>
              </div>
              <div className="trend-card">
                <span className="trend-label">Attendance Trend</span>
                <span className="trend-value">{studentAnalytics.attendance_trend || 'stable'}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (loading && !studentAnalytics) {
    return <LoadingSpinner />;
  }

  return (
    <div className="analytics-dashboard-page">
      <div className="page-header">
        <h1>Learning Analytics & Insights</h1>
        <p>Performance trends, early warning system, and actionable insights for improving student success</p>
      </div>

      <div className="toolbar">
        <button
          className="btn btn-secondary"
          onClick={handleRecalculateAll}
          disabled={recalculatingAll}
        >
          {recalculatingAll ? 'Recalculating...' : 'Recalculate All Analytics'}
        </button>
        <button className="btn btn-secondary" onClick={handleExportCSV}>
          <DownloadSimple size={16} /> Export CSV
        </button>
        <button className="btn btn-secondary" onClick={handleExportPDF}>
          <DownloadSimple size={16} /> Export PDF
        </button>
      </div>

      <div className="tabs">
        <button
          className={`tab-button ${activeTab === 'at-risk' ? 'active' : ''}`}
          onClick={() => setActiveTab('at-risk')}
        >
          <Warning size={20} />
          At-Risk Students
          {atRiskStudents.length > 0 && (
            <span className="badge">{atRiskStudents.length}</span>
          )}
        </button>
        <button
          className={`tab-button ${activeTab === 'classes' ? 'active' : ''}`}
          onClick={() => setActiveTab('classes')}
        >
          <BookOpen size={20} />
          Class Comparison
        </button>
        <button
          className={`tab-button ${activeTab === 'student' ? 'active' : ''}`}
          onClick={() => setActiveTab('student')}
        >
          <Users size={20} />
          Student Details
        </button>
      </div>

      <div className="analytics-content">
        {activeTab === 'at-risk' && <AtRiskDashboard />}
        {activeTab === 'classes' && <ClassComparisonDashboard />}
        {activeTab === 'student' && <StudentDetailsDashboard />}
      </div>

      <DialogModal
        isOpen={!!confirmDialog}
        onClose={() => setConfirmDialog(null)}
        title={confirmDialog?.title}
        message={confirmDialog?.message}
        type={confirmDialog?.type || 'warning'}
        primaryAction={{ label: confirmDialog?.confirmLabel || 'Confirm', onClick: () => confirmDialog?.onConfirm() }}
        secondaryAction={{ label: 'Cancel' }}
      />
    </div>
  );
};

export default AnalyticsDashboardPage;
